import { PiecePoints, PieChartSection, PiePiece } from './interface';
import { formatNumber } from 'accounting';

export const radiusRelative = 50;
const radiusInt = 20;
const radiusExt = radiusRelative - 5; // need to have some space to stroke selected piece
const radiusMid = (3 * radiusRelative) / 4;

const angleInitial = -0.5;
const lowestOpacity = 0.1;

const formatValue = (val: number): string => {
    if (!val) {
        return '0%';
    }

    const valueMain = val ? Math.abs(Math.floor(val)) : 0;

    if (valueMain > 0) {
        return formatNumber(valueMain, 0) + '%';
    }

    const valueDecimal = Math.max(Math.floor(val * 10) - valueMain * 10, 1);

    return `0.${valueDecimal}%`;
};

const convertPointsToPath = (points: PiecePoints, wideAngle: boolean): string =>
    `M ${points[3][0]},${points[3][1]} ` +
    `L ${points[0][0]},${points[0][1]} ` +
    `A ${radiusExt},${radiusExt} 0 ${wideAngle ? 1 : 0} 1 ${points[1][0]},${points[1][1]} ` +
    `L ${points[2][0]},${points[2][1]} ` +
    `A ${radiusInt},${radiusInt} 0 ${wideAngle ? 1 : 0} 0 ${points[3][0]},${points[3][1]} ` +
    `L ${points[0][0]},${points[0][1]} `;

export const convertSessionsToPieces = <T>(sections: PieChartSection<T>[]): PiePiece<T>[] => {
    const total = sections.reduce((sum, curr) => sum + curr.value, 0);
    const opacityStep = (1 - lowestOpacity) / (Math.max(sections.length, 2) - 1);
    let angleSum = 0;

    const pieces = sections.map((s, index) => {
        const percent = total ? s.value / total : 0;
        const angle = 2 * Math.PI * percent;
        const angleStart = angleInitial + angleSum;
        const angleMid = angleStart + angle / 2;
        const angleEnd = angleStart + angle;
        const opacity = 1 - opacityStep * index;

        const points: PiecePoints | undefined = s.value
            ? [
                  [radiusExt * Math.sin(angleStart), -1 * radiusExt * Math.cos(angleStart)],
                  [radiusExt * Math.sin(angleEnd), -1 * radiusExt * Math.cos(angleEnd)],
                  [radiusInt * Math.sin(angleEnd), -1 * radiusInt * Math.cos(angleEnd)],
                  [radiusInt * Math.sin(angleStart), -1 * radiusInt * Math.cos(angleStart)],
                  [radiusMid * Math.sin(angleMid), -1 * radiusMid * Math.cos(angleMid)],
              ]
            : undefined;

        angleSum += angle;

        return {
            id: 'pie' + Math.round(Math.random() * 10000),
            title: s.title,
            text: formatValue(percent * 100),
            data: s.data,
            color: s.color || `rgba(0,76,195,${opacity.toFixed(2)})`,
            opacity,
            points,
            path: points
                ? convertPointsToPath(points, angleEnd - angleStart >= Math.PI)
                : undefined,
        };
    });

    if (sections.filter(s => !!s.value).length === 1) {
        const piece = pieces.find(p => !!p.points);

        if (piece) {
            piece.points = [
                [-radiusExt, radiusExt],
                [radiusExt, radiusExt],
                [radiusInt, -radiusInt],
                [-radiusInt, -radiusInt],
                [0, 0],
            ];

            piece.path =
                `M ${-radiusExt},${0} ` +
                `A ${radiusExt},${radiusExt} 0 1 1 ${radiusExt},${0} ` +
                `A ${radiusExt},${radiusExt} 0 1 1 ${-radiusExt},${0} `;
        }
    }

    return pieces;
};
